import {NgModel} from '@angular/forms';
import {DigitalPlanAttachment, DigitalPlanAttachmentLotNumbersField} from './digital-plan.model';
import {TenureService} from '../plan-information/tenure/tenure.service';

/**
 * One of the lot declarations the surveyor makes on the Digital Plan tab: the question, the Yes/No answer, and the lot
 * numbers the answer applies to. Digital plans carry no spatial data of their own, so none of these can be read out of
 * the plan the way a portal plan reads them from its lot types and acquisition transfer actions.
 *
 * <p>The lot numbers are the stored answer rather than something kept beside it - no list at all is a question nobody has
 * answered, an empty list is one answered as creating no such lots, and any lot number means it does - so there is no
 * second flag that could fall out of step with them.</p>
 */
export class LotDeclaration {
    /** A single entry in the field: a lot number, or two separated by a hyphen to give a range. */
    private static readonly ENTRY = /^\d{1,6}(-\d{1,6})?$/;

    /** The most lot numbers one entry may span. A plan cannot plausibly create more, so a wider range is a typo. */
    private static readonly MAX_RANGE = 50;

    /**
     * The Yes/No the surveyor has chosen, undefined until they choose. These are declarations they are making rather than
     * questions with a usual answer, so neither option starts selected. Kept beside the lot numbers only because Yes has
     * to survive the moment between choosing it and typing the first lot number, where the numbers alone still read as No.
     */
    answer: boolean;

    /** What the surveyor has typed, before it is expanded into lot numbers. */
    text = '';

    /** The plan the text was last seeded for, so opening another plan reseeds and editing this one does not. */
    private seededPlanLodgementId: number;

    constructor(
        readonly field: DigitalPlanAttachmentLotNumbersField,
        readonly question: string,
        readonly fieldLabel: string,
        /** How a declared lot is named back to the surveyor when the plan does not create it. */
        private readonly lotNoun: string,
        /** Whether to ask the question of this plan at all. */
        readonly applicable: () => boolean,
        /** The new lot numbers recorded against former tenure, which every declared lot has to be one of. */
        private readonly formerTenureNewLots: () => Set<number>,
        /**
         * Anything wrong with the declared lots that only the other declarations can tell - the same lot declared as two things
         * that exclude each other, say - or an empty string.
         */
        private readonly conflict: () => string,
        private readonly attachment: () => DigitalPlanAttachment,
        private readonly editable: () => boolean,
        private readonly tenureService: TenureService
    ) {}

    /** The lot numbers saved against the question, or undefined for a question nobody has answered. */
    get lots(): number[] {
        // The server sends an unanswered question as null rather than leaving the field out, so both become undefined here.
        return this.attachment()?.[this.field] ?? undefined;
    }

    /**
     * Seeds the answer and the field from what was saved, once per plan opened rather than on every check. From then on
     * they are what the surveyor is editing and the lot numbers are derived from them, so re-deriving the answer would
     * snap a fresh Yes back to No before they could type a lot number against it.
     */
    seed(planLodgementId: number) {
        if (!planLodgementId || planLodgementId === this.seededPlanLodgementId) {
            return;
        }

        this.seededPlanLodgementId = planLodgementId;
        this.answer = this.lots === undefined ? undefined : this.lots.length > 0;
        this.text = this.formatText();
    }

    /**
     * Discards a declaration the plan can no longer hold - the purpose has changed to one where the question is not asked,
     * or the digital plan attachment has gone. Left in place, a hidden question would keep generating IOFD conditions
     * against lots the plan no longer creates, and a hidden field would keep failing its own validation.
     */
    withdraw() {
        this.seededPlanLodgementId = undefined;
        this.answer = undefined;
        this.text = '';

        // Only when there is something to discard, because this runs on every change detection cycle.
        if (this.editable() && this.lots !== undefined) {
            this.setLots(undefined);
        }
    }

    /** Answering No withdraws any lots named against the question, which are no longer lots of this kind. */
    onChangeAnswer(answer: boolean) {
        this.answer = answer;

        if (!answer) {
            this.text = '';
        }

        this.onChangeText(this.text);
    }

    /**
     * Expands what the surveyor typed into individual lot numbers. Nothing is written while the field is invalid, so a
     * half-typed entry cannot wipe the lots already declared.
     */
    onChangeText(value: string) {
        this.text = value ?? '';

        if (this.textInvalid()) {
            return;
        }

        this.setLots(this.tenureService.parseNumbersFromRanges(this.entries().map(entry => ({value: entry}))));
    }

    /**
     * Collapses the field into ranges once the surveyor leaves it, so what is stored and what is shown agree. Deliberately
     * not done on every keystroke, which would rewrite the text under the caret as it is being typed.
     */
    onBlurText() {
        if (!this.textInvalid()) {
            this.text = this.formatText();
        }
    }

    /** Whether the question is still unanswered. Blocks validation, as every declaration has to be made before lodgement. */
    unanswered(): boolean {
        return this.answer === undefined;
    }

    /**
     * Whether the question has been answered Yes without naming any lots. Blocks validation from the moment the question is
     * answered, and is checked here rather than with the input's own required attribute because this tab never submits its
     * form, and because a form-level error would also block the unrelated DWG upload.
     */
    missing(): boolean {
        return this.answer === true && this.entries().length === 0;
    }

    /**
     * Whether to say so as well. Held back until the surveyor has actually used the field - answering Yes already shows the
     * required asterisk and disables validation, so flagging an error before they have had a chance to type would be
     * telling them off for a field they are on their way to filling in. Emptying a field they had filled in does report it,
     * because that is a correction rather than a first attempt.
     */
    showMissing(field: NgModel): boolean {
        return this.missing() && (field?.dirty || field?.touched);
    }

    /**
     * Whether the field holds anything that is not a lot number or an ascending lot number range. An empty field is not a
     * format error - it is {@link missing} that rejects it, leaving the two errors distinct. Ranges are accepted for
     * consistency with how lots are entered elsewhere, but a wide one is rejected rather than expanded, because a typo
     * would otherwise be stored as thousands of lots.
     */
    textInvalid(): boolean {
        return this.entries().some(entry => {
            if (!LotDeclaration.ENTRY.test(entry)) {
                return true;
            }
            const [from, to] = entry.split('-').map(Number);
            return to !== undefined && (to < from || to - from >= LotDeclaration.MAX_RANGE);
        });
    }

    /**
     * Anything wrong with the declared lots beyond their format and presence, which only the plan around them can tell.
     * Blocks validation as well as being shown against the field.
     */
    error(): string {
        return [this.notCreatedMessage(), this.conflict()].filter(message => message).join(' ');
    }

    /**
     * Declared lots are lots the plan creates, so each has to be one of the new lots recorded against former tenure - anything
     * else declares something about a lot that does not exist. Asked of every declaration, because a road or widening that
     * takes a whole former tenure parcel is recorded there as fully superseding it with no lot number, and so has no lot number
     * to declare here either.
     */
    private notCreatedMessage(): string {
        const formerTenureNewLots = this.formerTenureNewLots();
        const notCreated = (this.lots ?? []).filter(lot => lot != null && !formerTenureNewLots.has(lot));
        return notCreated.length ? `${this.lotNoun} ${notCreated.join(', ')} must match a new lot number entered in the former tenure section.` : '';
    }

    private setLots(lots: number[]) {
        const attachment = this.attachment();
        if (attachment) {
            attachment[this.field] = lots;
        }
    }

    /**
     * Renders the saved lot numbers as ranges, so `1, 2, 4, 5, 6` reads back as `1-2, 4-6`, matching how lots are
     * summarised in the former tenure section.
     */
    private formatText(): string {
        return this.tenureService.formatRanges(this.tenureService.toRanges(this.lots ?? []));
    }

    private entries(): string[] {
        return this.text
            .split(',')
            .map(entry => entry.trim())
            .filter(entry => entry.length > 0);
    }
}
